import {
  Component,
  Inject,
  OnInit,
  Renderer2,
  ChangeDetectionStrategy
} from '@angular/core';
import { UtilsService } from '../../../shared/utils.service';
import { AppService } from '../../../app.service';
import { ApiRequestService } from '../../../shared/api-request.service';
import { Router } from '@angular/router';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef
} from '@angular/material/dialog';
import { FolderLinkModel } from '../../../shared/folder-link.model';
import { NgForm } from '@angular/forms';
import { RBACService } from '../../../shared/rbac.service';

@Component({
  selector: 'app-account-docs-links-edit',
  templateUrl: './account-docs-links-edit.component.html',
  styleUrls: ['./account-docs-links-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AccountDocsLinksEditComponent implements OnInit {
  inProgress = false;

  folder_id: number;
  folder_link: FolderLinkModel = new FolderLinkModel();
  folderContext: string = 'account';

  constructor(
    public utils: UtilsService,
    public app: AppService,
    private api: ApiRequestService,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public dialogData: any,
    private dialogRef: MatDialogRef<AccountDocsLinksEditComponent>,
    public router: Router,
    public renderer: Renderer2,
    private rbac: RBACService
  ) {}

  ngOnInit() {
    // get the folder link id
    this.folder_link.id = this.dialogData.folder_link.id;

    // get the folder id
    this.folder_id = this.dialogData.folder_id;

    // get the folder context from parent
    this.folderContext = this.dialogData.folderContext || 'account';

    // Check permissions for edit mode (requires view + edit)
    if (this.folder_link.id) {
      const editPermissions = this.getEditPermissionsForContext(
        this.folderContext
      );
      if (!this.rbac.userCanAccess(editPermissions, true)) {
        this.rbac.showGenericAccessDeniedMessage(
          'You do not have permission to edit document links.'
        );
        this.dialogRef.close(false);
        return;
      }
      this.find(this.folder_link.id);
    }
  }

  private find(folder_link_id: number) {
    this.api
      .makeRequest(
        'get',
        `v2/folders/${this.folder_id}/links/${folder_link_id}`
      )
      .then((response) => {
        this.folder_link = response;
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  async onSubmit(form: NgForm) {
    // Do not process if already in progress.
    if (this.inProgress) {
      return;
    }

    // Perform form validation.
    if (!form.valid) {
      this.utils.showFormValidationError('Please enter all required fields.');
      return;
    }

    this.inProgress = true;

    if (!this.folder_link.id) {
      this.create();
    } else {
      this.update();
    }
  }

  private create() {
    // Links are document metadata - creating links requires document EDIT permission
    const editPermissions = this.getEditPermissionsForContext(
      this.folderContext
    );
    if (!this.rbac.userCanAccess(editPermissions, true)) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to create document links.'
      );
      this.dialogRef.close(false);
      return;
    }

    this.api
      .makeRequest(
        'post',
        `v2/folders/${this.folder_id}/links`,
        this.folder_link,
        {}
      )
      .then((response) => {
        this.onSuccess(`A new Document Link was created.`);
        this.folder_link = response;
      })
      .finally(() => {
        this.inProgress = false;
      });
  }

  private update() {
    // Links are document metadata - editing links requires document EDIT permission
    const editPermissions = this.getEditPermissionsForContext(
      this.folderContext
    );
    if (!this.rbac.userCanAccess(editPermissions, true)) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to edit document links.'
      );
      this.dialogRef.close(false);
      return;
    }

    this.api
      .makeRequest(
        'put',
        `v2/folders/${this.folder_id}/links/${this.folder_link.id}`,
        this.folder_link,
        {}
      )
      .then((response) => {
        this.onSuccess(`The Document Link was updated.`);
        this.folder_link = response;
      })
      .finally(() => {
        this.inProgress = false;
      });
  }

  public onDelete() {
    // Links are document metadata - deleting links requires document EDIT permission
    const editPermissions = this.getEditPermissionsForContext(
      this.folderContext
    );
    if (!this.rbac.userCanAccess(editPermissions, true)) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete document links.'
      );
      this.dialogRef.close(false);
      return;
    }

    this.utils.showModal(
      'Delete Document Link',
      'Are you sure you want to delete the Document Link?',
      () => {
        this.api
          .makeRequest(
            'delete',
            `v2/folders/${this.folder_id}/links/${this.folder_link.id}`
          )
          .then(() => {
            this.onSuccess('The Document Link was deleted.');
          });
      }
    );
  }

  /**
   * Get edit permissions for the given context.
   * Links are document metadata - all link CRUD operations use document EDIT permission.
   */
  private getEditPermissionsForContext(context: string): string[] {
    switch (context) {
      case 'account':
        return [
          RBACService.getPermission(
            RBACService.RBAC_F.F_ACCOUNT_COMPANY_DOCUMENTS,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_ACCOUNT_COMPANY_DOCUMENTS,
            RBACService.RBAC_A.A_EDIT
          )
        ];
      case 'template':
        return [
          RBACService.getPermission(
            RBACService.RBAC_F.F_TEMPLATES_DOCUMENTS,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_TEMPLATES_DOCUMENTS,
            RBACService.RBAC_A.A_EDIT
          )
        ];
      case 'site':
        return [
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_DOCUMENTS,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_SITES_DOCUMENTS,
            RBACService.RBAC_A.A_EDIT
          )
        ];
      case 'employee':
        return [
          RBACService.getPermission(
            RBACService.RBAC_F.F_EMPLOYEES,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_EMPLOYEES_TRAINING_COMPETENCIES,
            RBACService.RBAC_A.A_VIEW
          ),
          RBACService.getPermission(
            RBACService.RBAC_F.F_EMPLOYEES_TRAINING_COMPETENCIES,
            RBACService.RBAC_A.A_EDIT
          )
        ];
      default:
        return [];
    }
  }

  private onSuccess(message: string) {
    this.utils.showToast(message);
    this.dialogRef.close(true);
  }

  // Expose RBACService to template
  protected readonly RBACService = RBACService;
}
